<template>
  <div class="authorization-page">
    <!-- Impersonation Banner -->
    <div v-if="isImpersonating" class="alert alert-warning alert-dismissible fade show mb-3" role="alert">
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <i class="bi bi-person-badge me-2"></i>
          <strong>Impersonating:</strong> {{ impersonatedUser.name }} ({{ impersonatedUser.email }})
        </div>
        <button type="button" class="btn btn-sm btn-outline-dark" @click="stopImpersonation">
          <i class="bi bi-arrow-left me-1"></i>Stop Impersonation
        </button>
      </div>
    </div>
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    <div v-else class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex align-items-center justify-content-between">
            <h4 class="card-title mb-0">
              <i class="bi bi-shield-check me-2"></i>Authorization Management
            </h4>
          </div>
          <div class="card-body">
            <!-- Tabs Navigation -->
            <ul class="nav nav-tabs mb-4">
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'users' }" @click="activeTab = 'users'" href="#">
                  <i class="bi bi-people me-2"></i>Users ({{ users.length }})
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'roles' }" @click="activeTab = 'roles'" href="#">
                  <i class="bi bi-shield me-2"></i>Roles ({{ roles.length }})
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'audit' }" @click="activeTab = 'audit'" href="#">
                  <i class="bi bi-clock-history me-2"></i>Audit Log
                </a>
              </li>
            </ul>
            <!-- Users Tab -->
            <div v-if="activeTab === 'users'" class="tab-content">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div class="d-flex gap-2">
                  <input type="text" class="form-control" placeholder="Search users..." v-model="userSearch" style="width: 300px;">
                  <select class="form-select" v-model="userRoleFilter" style="width: 200px;">
                    <option value="">All Roles</option>
                    <option v-for="role in roles" :key="role.id" :value="role.name">{{ role.name }}</option>
                    <option value="No Role">No Roles</option>
                  </select>
                </div>
                <button class="btn btn-primary" @click="showAddUserModal">
                  <i class="bi bi-plus me-2"></i>Add User
                </button>
              </div>
              <div class="table-responsive actions-table-container">
                <table class="table table-hover">
                  <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(user, index) in filteredUsers" :key="user.id">
                    <td>
                      <div class="d-flex align-items-center">
                        <img :src="getUserAvatar(user)" alt="Avatar" class="rounded-circle me-2" style="width: 40px; height: 40px;">
                        <div>
                          <div class="fw-semibold">{{ user.name }}</div>
                          <small class="text-muted">{{ user.title || 'No title' }}</small>
                        </div>
                      </div>
                    </td>
                    <td>{{ user.email }}</td>
                    <td>
                      <span class="badge" :class="getRoleBadgeClass(getUserRole(user))">{{ getUserRole(user) }}</span>
                    </td>
                    <td>
                      <span class="badge" :class="getUserStatusClass(user)">{{ getUserStatus(user) }}</span>
                    </td>
                    <td class="actions-cell">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary action-menu-toggle"
                        :aria-expanded="activeActionMenuUserId === user.id"
                        :ref="el => setActionMenuButtonRef(el, user.id)"
                        @click.stop="toggleActionMenu(user, index)"
                      >
                        <i class="bi bi-three-dots"></i>
                      </button>
                    </td>
                  </tr>
                  </tbody>
                </table>
                <div class="d-flex flex-wrap justify-content-between align-items-center mt-3 gap-2">
                  <!-- Results Info -->
                  <div class="pagination-info">
                    <small class="text-muted">
                      Showing {{ usersStartRecord }} to {{ usersEndRecord }} of {{ totalUserRecords }} users
                      <span v-if="userSearch || userRoleFilter">
                        (filtered from {{ users.length }} total users)
                      </span>
                    </small>
                  </div>

                  <!-- Page Size Selector -->
                  <div class="page-size-selector">
                    <label class="form-label me-2 mb-0">Show:</label>
                    <select
                      class="form-select form-select-sm"
                      v-model.number="usersPerPage"
                      @change="changeUsersPageSize"
                      style="width: 80px;"
                    >
                      <option :value="10">10</option>
                      <option :value="25">25</option>
                      <option :value="50">50</option>
                      <option :value="100">100</option>
                    </select>
                  </div>

                  <!-- Pagination Navigation -->
                  <nav aria-label="Users pagination">
                    <ul class="pagination pagination-sm mb-0">
                      <!-- First -->
                      <li class="page-item" :class="{ disabled: usersPage === 1 }">
                        <button class="page-link" @click="goToUsersPage(1)" :disabled="usersPage === 1">
                          <i class="bi bi-chevron-double-left"></i>
                        </button>
                      </li>

                      <!-- Previous -->
                      <li class="page-item" :class="{ disabled: usersPage === 1 }">
                        <button class="page-link" @click="goToUsersPage(usersPage - 1)" :disabled="usersPage === 1">
                          <i class="bi bi-chevron-left"></i>
                        </button>
                      </li>

                      <!-- Page Numbers with ellipsis -->
                      <li
                        v-for="page in usersVisiblePages"
                        :key="page + '-users'"
                        class="page-item"
                        :class="{ active: page === usersPage, disabled: page === '...'}"
                      >
                        <button
                          class="page-link"
                          :disabled="page === '...'"
                          @click="page !== '...' && goToUsersPage(page)"
                        >
                          {{ page }}
                        </button>
                      </li>

                      <!-- Next -->
                      <li class="page-item" :class="{ disabled: usersPage === totalUserPages }">
                        <button class="page-link" @click="goToUsersPage(usersPage + 1)" :disabled="usersPage === totalUserPages">
                          <i class="bi bi-chevron-right"></i>
                        </button>
                      </li>

                      <!-- Last -->
                      <li class="page-item" :class="{ disabled: usersPage === totalUserPages }">
                        <button class="page-link" @click="goToUsersPage(totalUserPages)" :disabled="usersPage === totalUserPages">
                          <i class="bi bi-chevron-double-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
            <!-- Roles Tab -->
            <div v-if="activeTab === 'roles'" class="tab-content">
              <div class="row">
                <div v-for="role in roles" :key="role.id" class="col-md-6 col-lg-4 mb-4">
                  <div class="card h-100">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-start mb-3">
                        <h5 class="card-title">{{ role.name }}</h5>
                      </div>
                      <p class="card-text text-muted">{{ role.description }}</p>
                      <div class="mb-3">
                        <small class="text-muted">Users: {{ role.userCount }}</small>
                      </div>
                      <div class="mb-3">
                        <h6>Permissions:</h6>
                        <div class="d-flex flex-wrap gap-1">
                          <span v-for="permission in role.permissions" :key="permission" class="badge bg-secondary">
                            {{ permission }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Audit Log Tab -->
            <div v-if="isSuperAdmin && activeTab === 'audit'" class="tab-content">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div class="d-flex gap-2">
                  <input type="date" class="form-control" v-model="auditDateFrom" style="width: 200px;">
                  <input type="date" class="form-control" v-model="auditDateTo" style="width: 200px;">
                  <select class="form-select" v-model="auditActionFilter" style="width: 200px;">
                    <option value="">All Actions</option>
                    <option value="login">Login</option>
                    <option value="logout">Logout</option>
                    <option value="create">Create</option>
                    <option value="update">Update</option>
                    <option value="delete">Delete</option>
                    <option value="impersonate">Impersonate</option>
                  </select>
                </div>
<!--                <button class="btn btn-outline-primary" @click="exportAuditLog">-->
<!--                  <i class="bi bi-download me-2"></i>Export-->
<!--                </button>-->
              </div>

              <div class="audit-table-container">
                <!-- Table -->
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>User</th>
                      <th>Action</th>
                      <th>Resource</th>
                      <th>IP Address</th>
                      <th>Details</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="log in paginatedAuditLogs" :key="log.id">
                      <td>{{ formatDateTime(log.timestamp) }}</td>
                      <td>{{ log.user }}</td>
                      <td>
                        <span class="badge" :class="getActionBadgeClass(log.action)">{{ log.action }}</span>
                      </td>
                      <td>{{ log.resource }}</td>
                      <td>{{ log.ipAddress }}</td>
                      <td>
                        <button class="btn btn-sm btn-outline-info" @click="viewAuditDetails(log)">
                          <i class="bi bi-eye"></i>
                        </button>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination Controls -->
                <div class="d-flex justify-content-between align-items-center mt-3">
                  <!-- Results Info -->
                  <div class="pagination-info">
                    <small class="text-muted">
                      Showing {{ startRecord }} to {{ endRecord }} of {{ totalRecords }} entries
                      <span v-if="auditActionFilter || auditDateFrom || auditDateTo">
          (filtered from {{ auditLogs.length }} total entries)
        </span>
                    </small>
                  </div>

                  <!-- Page Size Selector -->
                  <div class="page-size-selector">
                    <label class="form-label me-2 mb-0">Show:</label>
                    <select class="form-select form-select-sm" v-model="auditPageSize" @change="changePageSize" style="width: 80px;">
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>

                  <!-- Pagination Navigation -->
                  <nav aria-label="Audit logs pagination">
                    <ul class="pagination pagination-sm mb-0">
                      <!-- First Page -->
                      <li class="page-item" :class="{ disabled: auditCurrentPage === 1 }">
                        <button class="page-link" @click="goToPage(1)" :disabled="auditCurrentPage === 1">
                          <i class="bi bi-chevron-double-left"></i>
                        </button>
                      </li>

                      <!-- Previous Page -->
                      <li class="page-item" :class="{ disabled: auditCurrentPage === 1 }">
                        <button class="page-link" @click="goToPage(auditCurrentPage - 1)" :disabled="auditCurrentPage === 1">
                          <i class="bi bi-chevron-left"></i>
                        </button>
                      </li>

                      <!-- Page Numbers -->
                      <li
                        v-for="page in visiblePages"
                        :key="page"
                        class="page-item"
                        :class="{ active: page === auditCurrentPage }"
                      >
                        <button class="page-link" @click="goToPage(page)">
                          {{ page }}
                        </button>
                      </li>

                      <!-- Next Page -->
                      <li class="page-item" :class="{ disabled: auditCurrentPage === totalPages }">
                        <button class="page-link" @click="goToPage(auditCurrentPage + 1)" :disabled="auditCurrentPage === totalPages">
                          <i class="bi bi-chevron-right"></i>
                        </button>
                      </li>

                      <!-- Last Page -->
                      <li class="page-item" :class="{ disabled: auditCurrentPage === totalPages }">
                        <button class="page-link" @click="goToPage(totalPages)" :disabled="auditCurrentPage === totalPages">
                          <i class="bi bi-chevron-double-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>

                <!-- No Data Message -->
                <div v-if="filteredAuditLogs.length === 0" class="text-center py-4">
                  <div class="text-muted">
                    <i class="bi bi-inbox-fill fs-1 d-block mb-2"></i>
                    <p class="mb-0">No audit logs found</p>
                    <small v-if="auditActionFilter || auditDateFrom || auditDateTo">
                      Try adjusting your filters to see more results
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- User Modal -->
    <div
      class="modal fade"
      id="userModal"
      ref="userModal"
      :class="{ show: showUserModal, 'd-block': showUserModal }"
      v-if="showUserModal"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingUser ? 'Edit' : 'Add' }} User</h5>
            <button type="button" class="btn-close" @click="showUserModal = false"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveUser">
              <!-- Profile Image Upload -->
              <div class="text-center mb-4">
                <div class="profile-image-container mb-3" style="padding-right: 1.5rem; position: relative; display: inline-block;">
                  <img
                    :src="pendingCroppedBase64 || getModalAvatarUrl()"
                    alt="Profile"
                    class="rounded-circle"
                    style="width: 120px; height: 120px; object-fit: cover;"
                    @error="onModalAvatarError($event)">
                  <span
                    v-if="pendingCroppedBase64"
                    class="badge bg-warning text-dark"
                    style="position: absolute; bottom: 0; left: 50%; transform: translateX(-50%); font-size: 0.65rem; white-space: nowrap;"
                  >Pending save</span>
                </div>
                <div>
                  <input
                    type="file"
                    ref="avatarInput"
                    @change="onAvatarFileSelected"
                    accept="image/*"
                    style="display: none;"
                  >
                  <button
                    type="button"
                    class="btn btn-info btn-sm"
                    @click="$refs.avatarInput.click()"
                  >
                    <i class="bi bi-person-circle me-2"></i>SET PROFILE IMAGE
                  </button>
                  <small v-if="!editingUser" class="d-block text-muted mt-1">
                    Image will be uploaded automatically when the fellow is saved.
                  </small>
                </div>
              </div>
              <!-- Basic Information -->
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Full Name</label>
                    <input type="text" class="form-control" v-model="userForm.name" placeholder="John Doe" required>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Email</label>
                    <input type="email" class="form-control" v-model="userForm.email" placeholder="john.doe@example.com" required>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Title</label>
                    <select
                      class="form-select"
                      v-model="userForm.title"
                    >
                      <option value="">Select Title</option>
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Ms">Ms</option>
                      <option value="Dr">Dr</option>
                      <option value="Associate Professor">Associate Professor</option>
                      <option value="Professor">Professor</option>
                      <option value="Public">Public</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Password</label>
                    <div class="input-group">
                      <input
                        :type="showPassword ? 'text' : 'password'"
                        class="form-control"
                        v-model="userForm.password"
                        placeholder="••••••••"
                        :required="!editingUser"
                      >

                      <button
                        class="btn btn-outline-secondary"
                        type="button"
                        @click="showPassword = !showPassword"
                      >
                        <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                      </button>
                    </div>

                  </div>
                </div>
              </div>
              <!-- EDCTP Fellowship Section -->
              <div class="card mb-4">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <h6 class="mb-0">EDCTP Fellowship Details</h6>
                  <!-- Tabs for fellowship entries -->
                  <ul class="nav nav-tabs card-header-tabs" v-if="fellowshipEntries.length > 1">
                    <li
                      v-for="(entry, index) in fellowshipEntries"
                      :key="'fellowship-tab-' + index"
                      class="nav-item"
                    >
                      <button
                        type="button"
                        class="nav-link"
                        :class="{ active: activeFellowshipTab === index }"
                        @click="activeFellowshipTab = index"
                      >
                        Fellowship {{ index + 1 }}
                      </button>
                    </li>
                  </ul>
                </div>

                <div class="card-body">
                  <div
                    v-for="(entry, index) in fellowshipEntries"
                    :key="'fellowship-entry-' + index"
                    v-show="activeFellowshipTab === index"
                  >
                    <div class="row">
                      <div class="col-md-6">
                        <div class="mb-3">
                          <label class="form-label">Gender</label>
                          <select class="form-select" v-model="entry.gender">
                            <option value="">Select Gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="mb-3">
                          <label class="form-label">EDCTP Programme</label>
                          <select
                            class="form-select"
                            v-model="entry.edctp_programme"
                            @change="onProgrammeChange(index)"
                          >
                            <option value="">Select Programme</option>
                            <option value="1">EDCTP1</option>
                            <option value="2">EDCTP2</option>
                          </select>
                          <small class="text-muted d-block mt-1">
                            Max 2 entries per user. A second entry is only created when the
                            first is changed to EDCTP2.
                          </small>
                        </div>
                      </div>
                    </div>

                    <div class="row">
                      <div class="col-md-6">
                        <div class="mb-3">
                          <label class="form-label">EDCTP Call</label>
                          <select class="form-select" v-model="entry.edctp_call">
                            <option value="">Select Call</option>
                            <option value="1">Career Development Fellowship (CDF)</option>
                            <option value="2">Senior Fellowship (SF)</option>
                            <option value="3">EDCTP Clinical Research & Development Fellowship (R&D F)</option>
                            <option value="4">EDCTP-AREF Preparatory Fellowships (PF)</option>
                            <option value="5">Senior Fellowship Plus (SFP)</option>
                            <option value="6">Disease Outbreak and Epidemic Response (EPI Fellows)</option>
                            <option value="7">Female PhD Fellowships</option>
                            <option value="8">EDCTP Master's Fellowship</option>
                          </select>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="mb-3">
                          <label class="form-label">EDCTP Call Year</label>
                          <select class="form-control" v-model="entry.edctp_call_date">
                            <option disabled value="">Select Year</option>
                            <option v-for="year in years" :key="year" :value="year">
                              {{ year }}
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <!-- Fellowship Information -->
                    <div class="mb-3">
                      <div class="row">
                        <div class="col-md-6">
                          <label class="form-label">Fellowship Project Title</label>
                          <input
                            type="text"
                            class="form-control mb-2"
                            v-model="entry.edctp_project_title"
                            placeholder="EDCTP Project Title"
                          >
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">Fellowship Project Code</label>
                          <input
                            type="text"
                            class="form-control mb-2"
                            v-model="entry.edctp_project_code"
                            placeholder="EDCTP Project Code"
                          >
                        </div>
                      </div>
                    </div>

                    <!-- Date Range -->
                    <div class="row">
                      <div class="col-md-6">
                        <div class="mb-3">
                          <label class="form-label">Start Date</label>
                          <input
                            type="date"
                            class="form-control"
                            v-model="entry.start_date"
                          >
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="mb-3">
                          <label class="form-label">End Date</label>
                          <input
                            type="date"
                            class="form-control"
                            v-model="entry.end_date"
                          >
                        </div>
                      </div>
                    </div>

                    <!-- Text Areas -->
                    <div class="mb-3">
                      <label class="form-label">Objectives</label>
                      <textarea
                        class="form-control"
                        rows="4"
                        v-model="entry.objectives"
                        placeholder="Enter objectives..."
                      ></textarea>
                    </div>
                    <div class="mb-3">
                      <label class="form-label">Study Design</label>
                      <textarea
                        class="form-control"
                        rows="4"
                        v-model="entry.study_design"
                        placeholder="Enter study design..."
                      ></textarea>
                    </div>
                    <div class="mb-3">
                      <label class="form-label">Project Summary</label>
                      <textarea
                        class="form-control"
                        rows="4"
                        v-model="entry.project_summary"
                        placeholder="Enter project summary..."
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showUserModal = false">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveUser" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ saving ? 'Saving...' : 'Save User' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Assign Role Modal -->
    <div class="modal fade" :class="{ show: showAssignRoleModalFlag, 'd-block': showAssignRoleModalFlag }"
         v-if="showAssignRoleModalFlag"
         data-bs-backdrop="static"
         data-bs-keyboard="false"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-shield me-2"></i>Assign Role
            </h5>
            <button type="button" class="btn-close" @click="showAssignRoleModalFlag = false"></button>
          </div>
          <div class="modal-body">
            <div class="d-flex align-items-center mb-3" v-if="selectedUserForRole">
              <img :src="getUserAvatar(selectedUserForRole)" alt="Avatar" class="rounded-circle me-3" style="width: 50px; height: 50px;">
              <div>
                <div class="fw-semibold">{{ selectedUserForRole.name }}</div>
                <div class="text-muted">{{ selectedUserForRole.email }}</div>
                <span class="badge" :class="getRoleBadgeClass(getUserRole(selectedUserForRole))">Current: {{ getUserRole(selectedUserForRole) }}</span>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Select New Role:</label>
              <select class="form-select" v-model="newRoleForUser">
                <option value="">Select Role</option>
                <option v-for="role in roles" :key="role.id" :value="role.name">{{ role.name }}</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Reason for role change (optional):</label>
              <textarea class="form-control" v-model="roleChangeReason" placeholder="Enter reason for role change..." rows="3"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showAssignRoleModalFlag = false">Cancel</button>
            <button type="button" class="btn btn-primary" @click="assignRole" :disabled="!newRoleForUser">
              <i class="bi bi-shield me-2"></i>Assign Role
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Assign Working Group Modal -->
    <div class="modal fade" :class="{ show: showAssignWorkingGroupModalFlag, 'd-block': showAssignWorkingGroupModalFlag }"
         v-if="showAssignWorkingGroupModalFlag"
         data-bs-backdrop="static"
         data-bs-keyboard="false"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-people me-2"></i>Assign Working Group Administrator
            </h5>
            <button type="button" class="btn-close" @click="showAssignWorkingGroupModalFlag = false"></button>
          </div>
          <div class="modal-body">
            <div class="d-flex align-items-center mb-3" v-if="selectedUserForWorkingGroup">
              <img :src="getUserAvatar(selectedUserForWorkingGroup)" alt="Avatar" class="rounded-circle me-3" style="width: 50px; height: 50px;">
              <div>
                <div class="fw-semibold">{{ selectedUserForWorkingGroup.name }}</div>
                <div class="text-muted">{{ selectedUserForWorkingGroup.email }}</div>
                <div class="text-muted small">Current Groups: {{ selectedUserForWorkingGroup.workingGroups ? selectedUserForWorkingGroup.workingGroups.join(', ') : 'None' }}</div>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Select Working Groups:</label>
              <div class="form-check" v-for="group in workingGroups" :key="group.id">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :value="group.id"
                  v-model="selectedWorkingGroups"
                />
                <label class="form-check-label">
                  {{ group.name }}
                </label>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Reason for assignment (optional):</label>
              <textarea class="form-control" v-model="workingGroupAssignmentReason" placeholder="Enter reason for working group assignment..." rows="3"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showAssignWorkingGroupModalFlag = false">Cancel</button>
            <button type="button" class="btn btn-primary" @click="assignWorkingGroup">
              <i class="bi bi-people me-2"></i>Assign Working Groups
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Delete Confirmation Modal -->
    <div class="modal fade" :class="{ show: showDeleteModalFlag, 'd-block': showDeleteModalFlag }"
         v-if="showDeleteModalFlag"
         data-bs-backdrop="static"
         data-bs-keyboard="false"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-danger">
              <i class="bi bi-exclamation-triangle me-2"></i>Delete User
            </h5>
            <button type="button" class="btn-close" @click="showDeleteModalFlag = false"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-danger">
              <i class="bi bi-exclamation-triangle me-2"></i>
              <strong>Warning:</strong> This action will DISABLE the user.
            </div>
            <p>Are you sure you want to delete:</p>
            <div class="d-flex align-items-center mb-3" v-if="userToDelete">
              <img :src="getUserAvatar(userToDelete)" alt="Avatar" class="rounded-circle me-3" style="width: 50px; height: 50px;">
              <div>
                <div class="fw-semibold">{{ userToDelete.name }}</div>
                <div class="text-muted">{{ userToDelete.email }}</div>
                <span class="badge" :class="getRoleBadgeClass(getUserRole(userToDelete))">{{ getUserRole(userToDelete) }}</span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showDeleteModalFlag = false">Cancel</button>
            <button type="button" class="btn btn-danger" @click="confirmDeleteUser">
              <i class="bi bi-trash me-2"></i>Disable User
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Impersonation Modal -->
    <div class="modal fade" :class="{ show: showImpersonationModal, 'd-block': showImpersonationModal }"
         v-if="showImpersonationModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-person-badge me-2"></i>Confirm User Impersonation
            </h5>
            <button type="button" class="btn-close" @click="showImpersonationModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning">
              <i class="bi bi-exclamation-triangle me-2"></i>
              <strong>Warning:</strong> You are about to impersonate another user. This action will be logged for security purposes.
            </div>
            <p>Are you sure you want to impersonate:</p>
            <div class="d-flex align-items-center mb-3" v-if="userToImpersonate">
              <img :src="getUserAvatar(userToImpersonate)" alt="Avatar" class="rounded-circle me-3" style="width: 50px; height: 50px;">
              <div>
                <div class="fw-semibold">{{ userToImpersonate.name }}</div>
                <div class="text-muted">{{ userToImpersonate.email }}</div>
                <span class="badge" :class="getRoleBadgeClass(getUserRole(userToImpersonate))">{{ getUserRole(userToImpersonate) }}</span>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Reason for impersonation (optional):</label>
              <textarea class="form-control" v-model="impersonationReason" placeholder="Enter reason for impersonation..." rows="3"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showImpersonationModal = false">Cancel</button>
            <button type="button" class="btn btn-warning" @click="startImpersonation">
              <i class="bi bi-person-badge me-2"></i>Start Impersonation
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal fade" :class="{ show: showAuditDetailsModal, 'd-block': showAuditDetailsModal }"
         v-if="showAuditDetailsModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-eye me-2"></i>Changes Made
            </h5>
            <button type="button" class="btn-close" @click="showAuditDetailsModal = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedAuditLog">
              <!-- Basic Information -->
              <div class="row mb-4">
                <div class="col-12">
                  <div class="info-card">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="text-muted mb-1">Action Details</h6>
                        <div class="info-item">
                          <span class="badge" :class="getActionBadgeClass(selectedAuditLog.action)">
                            {{ selectedAuditLog.action }}
                          </span>
                          <strong>{{ formatDateTime(selectedAuditLog.timestamp) }}</strong>
                          <span class="text-muted">by {{ selectedAuditLog.user }}</span>
                          <span class="text-muted">{{ selectedAuditLog.description }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showAuditDetailsModal = false">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Profile Image Cropper -->
  <ProfileImageCropper
    v-model="showCropper"
    :image-src="cropperImageSrc"
    @cropped="onImageCropped"
    @cancel="cropperImageSrc = null"
  />
  <teleport to="body">
    <ul
      v-if="activeActionMenuUser"
      class="dropdown-menu show authorization-action-menu"
      :style="actionMenuStyle"
      @click.stop
    >
      <li v-if="!activeActionMenuUser.deleted_at">
        <a class="dropdown-item" href="#" @click.prevent="runActionMenuAction(() => editUser(activeActionMenuUser))">Edit</a>
      </li>
      <li v-if="!activeActionMenuUser.deleted_at">
        <a class="dropdown-item" href="#" @click.prevent="runActionMenuAction(() => sendPasswordReset(activeActionMenuUser))">Send Password Reset Link</a>
      </li>
      <li v-if="!activeActionMenuUser.deleted_at"><hr class="dropdown-divider"></li>
      <li v-if="!activeActionMenuUser.deleted_at">
        <a class="dropdown-item" href="#" @click.prevent="runActionMenuAction(() => showAssignRoleModal(activeActionMenuUser))">Assign Role</a>
      </li>
      <li v-if="!activeActionMenuUser.deleted_at">
        <a class="dropdown-item" href="#" @click.prevent="runActionMenuAction(() => showAssignWorkingGroupModal(activeActionMenuUser))">Assign Working Group Administrator</a>
      </li>
      <li v-if="!activeActionMenuUser.deleted_at"><hr class="dropdown-divider"></li>
      <li v-if="isSuperAdmin && activeActionMenuUser.id !== currentUser?.id && !activeActionMenuUser.deleted_at">
        <a class="dropdown-item" href="#" @click.prevent="runActionMenuAction(() => confirmImpersonation(activeActionMenuUser))">
          Impersonate
        </a>
      </li>
      <li v-if="!activeActionMenuUser.deleted_at">
        <a class="dropdown-item text-danger" href="#" @click.prevent="runActionMenuAction(() => deleteUser(activeActionMenuUser))">Disable</a>
      </li>
      <li v-if="activeActionMenuUser.deleted_at">
        <a class="dropdown-item text-success" href="#" @click.prevent="runActionMenuAction(() => restoreUser(activeActionMenuUser))">Restore</a>
      </li>
    </ul>
  </teleport>
  <!-- Toast Notification -->
  <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">
    <div class="toast" :class="{ show: toast.show, 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error' }" role="alert">
      <div class="toast-header" :class="{ 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error', 'text-white': true }">
        <i class="bi me-2" :class="toast.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
        <strong class="me-auto">{{ toast.type === 'success' ? 'Success' : 'Error' }}</strong>
        <button type="button" class="btn-close btn-close-white" @click="toast.show = false"></button>
      </div>
      <div class="toast-body text-white">
        {{ toast.message }}
      </div>
    </div>
  </div>
</template>

<script>
import UserService from '@/services/users/UserService';
import ApiService from '@/services/api';
import AuthenticationService from '@/services/authentication/AuthenticationService';
import { avatarMixin } from '@/mixins/avatarMixin';
import WorkingGroupService from '@/services/workinggroups/WorkingGroupServices';
import ProfileImageCropper from '@/components/common/ProfileImageCropper.vue';

export default {
  name: 'Authorization',
  components: { ProfileImageCropper },
  mixins: [avatarMixin],
  data() {
    return {
      activeTab: 'users',
      usersPage: 1,
      usersPerPage: 25,
      userSearch: '',
      userRoleFilter: '',
      auditDateFrom: '',
      auditDateTo: '',
      auditActionFilter: '',
      showEditModal: false,
      showUserModal: false,
      showImpersonationModal: false,
      showAssignRoleModalFlag: false,
      showAssignWorkingGroupModalFlag: false,
      showDeleteModalFlag: false,
      editingUser: null,
      userToImpersonate: null,
      selectedUserForRole: null,
      selectedUserForWorkingGroup: null,
      userToDelete: null,
      impersonationReason: '',
      roleChangeReason: '',
      workingGroupAssignmentReason: '',
      newRoleForUser: '',
      selectedWorkingGroups: [],
      isImpersonating: false,
      impersonatedUser: null,
      originalUser: null,
      loading: false,
      saving: false,
      showPassword: false,
      auditCurrentPage: 1,
      auditPageSize: 25,
      users: [],
      currentUser: null,
      toast: {
        show: false,
        message: '',
        type: 'success'
      },
      // Base user fields ONLY (no fellowship fields here anymore)
      userForm: {
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        title: '',
        avatar: ''
      },
      // Profile image cropper state
      showCropper: false,
      cropperImageSrc: null,
      pendingCroppedBase64: null,
      activeActionMenuUserId: null,
      activeActionMenuIndex: null,
      actionMenuPosition: {
        top: 0,
        right: 0
      },
      actionMenuButtonRefs: {},
      // Fellowship entries (max 2)
      fellowshipEntries: [
        {
          id: null,
          gender: '',
          edctp_programme: '',
          edctp_call: '',
          edctp_call_date: '',
          edctp_project_title: '',
          edctp_project_code: '',
          start_date: '',
          end_date: '',
          objectives: '',
          study_design: '',
          project_summary: ''
        }
      ],
      activeFellowshipTab: 0,

      roles: [
        {
          id: 1,
          name: 'Super Admin',
          description: 'Full system access with all permissions',
          userCount: 3,
          permissions: ['Full System Access']
        },
        {
          id: 2,
          name: 'EDCTP Admin',
          description: 'User, Working Group and Content Management',
          userCount: 8,
          permissions: ['Limited Authorization Management Access', 'Assigned Working Group Management Access', 'Basic System Access']
        },
        {
          id: 3,
          name: 'CMS Admin',
          description: 'Content Management',
          userCount: 45,
          permissions: ['CMS Management Access', 'Basic System Access']
        },
        {
          id: 4,
          name: 'Fellow',
          description: 'Standard system user access',
          userCount: 45,
          permissions: ['Basic System Access']
        },
        {
          id: 5,
          name: 'Testing User',
          description: 'Standard system user access',
          userCount: 45,
          permissions: ['Basic System Access(But dont fall under Fellow data)']
        }
      ],
      auditLogs: [],
      workingGroups: [],
      showAuditDetailsModal: false,
      selectedAuditLog: null,
      years: this.generateYears(1990, 2030),
    }
  },

  computed: {
    // All filtered users, no pagination
    filteredUsersAll() {
      let filtered = this.users

      // Search filter
      if (this.userSearch) {
        const q = this.userSearch.toLowerCase()
        filtered = filtered.filter(user =>
          user.name.toLowerCase().includes(q) ||
          user.email.toLowerCase().includes(q)
        )
      }

      // Role filter
      if (this.userRoleFilter) {
        filtered = filtered.filter(user => this.getUserRole(user) === this.userRoleFilter)
      }

      return filtered
    },

    // Page slice
    filteredUsers() {
      const start = (this.usersPage - 1) * this.usersPerPage
      const end = start + this.usersPerPage
      return this.filteredUsersAll.slice(start, end)
    },

    totalUserPages() {
      return Math.max(1, Math.ceil(this.filteredUsersAll.length / this.usersPerPage) || 1)
    },

    totalUserRecords() {
      return this.filteredUsersAll.length
    },

    usersStartRecord() {
      if (this.totalUserRecords === 0) return 0
      return (this.usersPage - 1) * this.usersPerPage + 1
    },

    usersEndRecord() {
      const end = this.usersPage * this.usersPerPage
      return Math.min(end, this.totalUserRecords)
    },

    usersVisiblePages() {
      const pages = []
      const total = this.totalUserPages
      const current = this.usersPage

      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i)
      } else if (current <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i)
        pages.push('...')
        pages.push(total)
      } else if (current >= total - 3) {
        pages.push(1)
        pages.push('...')
        for (let i = total - 4; i <= total; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = current - 1; i <= current + 1; i++) pages.push(i)
        pages.push('...')
        pages.push(total)
      }

      return pages
    },

    filteredAuditLogs() {
      let filtered = this.auditLogs
      if (this.auditActionFilter) {
        filtered = filtered.filter(log => log.action === this.auditActionFilter)
      }
      return filtered
    },

    isSuperAdmin() {
      if (!this.currentUser || !this.currentUser.roles || !Array.isArray(this.currentUser.roles)) {
        return false
      }
      return this.currentUser.roles.some(role => role.name === 'Super Admin')
    },

    paginatedAuditLogs() {
      const startIndex = (this.auditCurrentPage - 1) * this.auditPageSize
      const endIndex = startIndex + this.auditPageSize
      return this.filteredAuditLogs.slice(startIndex, endIndex)
    },

    totalPages() {
      return Math.ceil(this.filteredAuditLogs.length / this.auditPageSize)
    },

    totalRecords() {
      return this.filteredAuditLogs.length
    },

    startRecord() {
      if (this.totalRecords === 0) return 0
      return (this.auditCurrentPage - 1) * this.auditPageSize + 1
    },

    endRecord() {
      const end = this.auditCurrentPage * this.auditPageSize
      return Math.min(end, this.totalRecords)
    },

    visiblePages() {
      const pages = []
      const totalPages = this.totalPages
      const currentPage = this.auditCurrentPage

      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        if (currentPage <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i)
          }
          if (totalPages > 6) {
            pages.push('...')
            pages.push(totalPages)
          }
        } else if (currentPage >= totalPages - 3) {
          pages.push(1)
          if (totalPages > 6) {
            pages.push('...')
          }
          for (let i = totalPages - 4; i <= totalPages; i++) {
            pages.push(i)
          }
        } else {
          pages.push(1)
          pages.push('...')
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(totalPages)
        }
      }

      return pages.filter(page => page !== '...' || pages.indexOf(page) === pages.lastIndexOf(page))
    },

    showImpersonationBanner() {
      return this.isImpersonating && this.originalUser && this.impersonatedUser
    },

    activeActionMenuUser() {
      if (!this.activeActionMenuUserId) {
        return null
      }

      return this.users.find(user => user.id === this.activeActionMenuUserId) || null
    },

    actionMenuStyle() {
      return {
        position: 'fixed',
        top: `${this.actionMenuPosition.top}px`,
        right: `${this.actionMenuPosition.right}px`
      }
    }
  },

  async mounted() {
    const isImpersonating = localStorage.getItem('impersonation_active') === 'true'

    if (isImpersonating) {
      await this.checkImpersonationState()

      if (this.isImpersonating) {
        this.showToast.info(`Currently impersonating ${this.impersonatedUser?.name}`, {
          duration: 3000
        })
      }
    }

    await this.initializeComponent()
    this.watchAuditFilters()
    await this.loadWorkingGroups();

    document.addEventListener('click', this.handleDocumentClick)
    window.addEventListener('resize', this.handleViewportChange)
    window.addEventListener('scroll', this.closeActionMenu, true)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick)
    window.removeEventListener('resize', this.handleViewportChange)
    window.removeEventListener('scroll', this.closeActionMenu, true)
  },

  methods: {
    normalizeYear(value) {
      if (!value) return '';

      const str = String(value);

      // ISO date / YYYY-MM-DD formats
      if (str.includes('-')) {
        return str.substring(0, 4);
      }

      // Pure 4-digit year
      if (/^\d{4}$/.test(str)) {
        return str;
      }

      return '';
    },

    generateYears(start, end) {
      const list = [];
      for (let y = end; y >= start; y--) {
        list.push(y);
      }
      return list;
    },

    showToast(type, message) {
      this.toast.type = type;
      this.toast.message = message;
      this.toast.show = true;

      setTimeout(() => {
        this.toast.show = false;
      }, 3000);
    },

    async loadWorkingGroups() {
      try {
        const response = await WorkingGroupService.getWorkingGroups();
        console.log('Reponse', response)

        // JSON API structure normalization
        if (response?.Data?.data && Array.isArray(response.Data.data)) {
          this.workingGroups = response.Data.data;
        } else if (Array.isArray(response?.data)) {
          this.workingGroups = response.data;
        } else {
          console.warn("Unexpected working group response:", response);
          this.workingGroups = [];
        }
      } catch (error) {
        console.error("Failed to load working groups:", error);
        this.workingGroups = [];
      }
    },

    goToUsersPage(page) {
      if (typeof page !== 'number') return
      if (page < 1 || page > this.totalUserPages) return
      this.usersPage = page
    },

    changeUsersPageSize() {
      // When page size changes, reset to first page
      this.usersPage = 1
      this.closeActionMenu()
    },

    setActionMenuButtonRef(element, userId) {
      if (element) {
        this.actionMenuButtonRefs[userId] = element
        return
      }

      delete this.actionMenuButtonRefs[userId]
    },

    toggleActionMenu(user, index) {
      if (this.activeActionMenuUserId === user.id) {
        this.closeActionMenu()
        return
      }

      this.activeActionMenuUserId = user.id
      this.activeActionMenuIndex = index
      this.$nextTick(() => {
        this.updateActionMenuPosition()
      })
    },

    updateActionMenuPosition() {
      if (!this.activeActionMenuUserId) {
        return
      }

      const trigger = this.actionMenuButtonRefs[this.activeActionMenuUserId]
      if (!trigger) {
        this.closeActionMenu()
        return
      }

      const rect = trigger.getBoundingClientRect()
      const menuHeight = this.activeActionMenuUser?.deleted_at ? 96 : 232
      const gap = 6
      const spaceBelow = window.innerHeight - rect.bottom
      const openUp = spaceBelow < menuHeight && rect.top > menuHeight

      const right = window.innerWidth - rect.right
      const top = openUp ? rect.top - menuHeight - gap : rect.bottom + gap

      this.actionMenuPosition = { top, right }
    },

    handleDocumentClick(event) {
      if (!this.activeActionMenuUserId) {
        return
      }

      const trigger = this.actionMenuButtonRefs[this.activeActionMenuUserId]
      if (trigger && trigger.contains(event.target)) {
        return
      }

      if (event.target.closest('.authorization-action-menu')) {
        return
      }

      this.closeActionMenu()
    },

    handleViewportChange() {
      if (!this.activeActionMenuUserId) {
        return
      }

      this.updateActionMenuPosition()
    },

    runActionMenuAction(callback) {
      this.closeActionMenu()
      callback()
    },

    closeActionMenu() {
      this.activeActionMenuUserId = null
      this.activeActionMenuIndex = null
    },

    updateRoleUserCounts() {
      // Reset counts
      this.roles.forEach(role => (role.userCount = 0))

      // Loop through users and increment matching roles
      this.users.forEach(user => {
        if (!user.roles || user.roles.length === 0) return

        user.roles.forEach(userRole => {
          const roleName = userRole.name
          const matchedRole = this.roles.find(r => r.name === roleName)
          if (matchedRole) {
            matchedRole.userCount += 1
          }
        })
      })
    },

    // ==========================
    // INITIALIZATION
    // ==========================

    async initializeComponent() {
      this.loading = true
      try {
        try {
          await this.fetchCurrentUser()
        } catch (userError) {
          console.warn('Could not fetch current user, continuing anyway:', userError)
          this.currentUser = {
            id: 'unknown',
            name: 'Current User',
            email: 'user@example.com',
            roles: [{ name: 'Super Admin' }]
          }
        }

        await this.fetchUsers(false)

        try {
          await this.fetchAuditLogs()
        } catch (auditError) {
          console.warn('Could not fetch audit logs:', auditError)
          this.auditLogs = []
        }

      } catch (error) {
        console.error('Error initializing component:', error)
        this.showToast.error('Failed to load data') || console.error('Failed to load data')
      } finally {
        this.loading = false
      }
    },

    watchAuditFilters() {
      this.$watch('auditActionFilter', () => {
        this.fetchAuditLogs()
      })

      this.$watch('auditDateFrom', () => {
        this.fetchAuditLogs()
      })

      this.$watch('auditDateTo', () => {
        this.fetchAuditLogs()
      })
    },

    // ==========================
    // USER MANAGEMENT
    // ==========================

    async fetchCurrentUser() {
      try {
        const response = await UserService.getCurrentUser()
        if (response?.Data?.data?.user) {
          this.currentUser = response.Data.data.user
        } else if (response?.data?.user) {
          this.currentUser = response.data.user
        } else if (response?.user) {
          this.currentUser = response.user
        }

        if (this.currentUser && this.currentUser.id) {
          setTimeout(() => {
            this.checkImpersonationState()
          }, 100)
        }

      } catch (error) {
        console.error('Error fetching current user:', error)
        if (error?.response?.status === 401) {
          throw error
        }
      }
    },

    async fetchUsers(showLoading = true) {
      if (showLoading) {
        this.loading = true
      }
      try {
        const response = await UserService.getUsers()

        if (response?.status === 'success' && response.data) {
          this.users = response.data
        } else if (response?.Data?.status === 'success' && response.Data.data) {
          this.users = response.Data.data
        } else if (Array.isArray(response?.data)) {
          this.users = response.data
        } else if (Array.isArray(response)) {
          this.users = response
        } else {
          console.error('Unexpected response structure:', response)
          this.showToast('error', 'Unexpected response format from server')
          this.users = []
        }

        this.updateRoleUserCounts()

        if (this.isImpersonating && this.currentUser) {
          const impersonatedUserInList = this.users.find(u => u.id === this.currentUser.id)
          if (impersonatedUserInList) {
            this.currentUser = { ...this.currentUser, ...impersonatedUserInList }
          }
        }
      } catch (error) {
        console.error('Error fetching users:', error)
        this.showToast('error', 'Failed to fetch users')
        this.users = []
      } finally {
        if (showLoading) {
          this.loading = false
        }
      }
    },

    getUserRole(user) {
      if (user.roles && user.roles.length > 0) {
        return user.roles[0].name
      }
      return 'No Role'
    },

    getUserStatus(user) {
      return user.deleted_at ? 'Inactive' : 'Active'
    },

    getUserStatusClass(user) {
      return user.deleted_at ? 'bg-danger' : 'bg-success'
    },

    getRoleBadgeClass(role) {
      const classes = {
        'Super Admin': 'bg-danger',
        'EDCTP Admin': 'bg-warning',
        'CMS Admin': 'bg-primary',
        'Fellow': 'bg-success'
      }
      return classes[role] || 'bg-secondary'
    },

    // ==========================
    // IMPERSONATION METHODS
    // ==========================

    confirmImpersonation(user) {
      if (!this.isSuperAdmin) {
        this.showToast('error', 'Only Super Admins can impersonate users!')
        return
      }
      this.userToImpersonate = user
      this.impersonationReason = ''
      this.showImpersonationModal = true
    },

    async startImpersonation() {
      if (!this.userToImpersonate) return

      try {
        const response = await UserService.startImpersonation(
          this.userToImpersonate.id,
          this.impersonationReason
        )

        const data = response?.Data?.data || response?.data

        if (!data || !data.access_token) {
          throw new Error('No access token received from server')
        }

        this.showImpersonationModal = false
        this.userToImpersonate = null
        this.impersonationReason = ''

        localStorage.clear()
        sessionStorage.clear()

        ApiService.setAuthToken(data.access_token, 'access_token')

        const storedToken = ApiService.getAuthToken()
        if (!storedToken || storedToken !== data.access_token) {
          throw new Error('Failed to store impersonation token')
        }

        localStorage.setItem('impersonation_active', 'true')
        localStorage.setItem('original_user', JSON.stringify(data.original_user))
        localStorage.setItem('impersonated_user', JSON.stringify(data.impersonated_user))

        if (this.$store && data.impersonated_user) {
          this.$store.commit('auth/SET_USER', data.impersonated_user)
          this.$store.commit('auth/SET_IMPERSONATION', {
            isImpersonating: true,
            originalUser: data.original_user,
            impersonatedUser: data.impersonated_user
          })
        }

        this.isImpersonating = true
        this.originalUser = data.original_user
        this.impersonatedUser = data.impersonated_user
        this.currentUser = data.impersonated_user

          this.showToast('success', `Now impersonating ${data.impersonated_user.name}`)

          // Redirect to the impersonated user's dashboard without a full reload
          await this.$router.push('/my-profile')

      } catch (error) {
        console.error('❌ Impersonation failed:', error)

        this.showImpersonationModal = false
        this.userToImpersonate = null
        this.impersonationReason = ''

        const errorMessage = error?.response?.data?.message ||
          error?.response?.data?.Data?.message ||
          error?.message ||
          'Failed to start impersonation'

        this.showToast('error', errorMessage)
      }
    },

    async stopImpersonation() {
      try {
        const response = await UserService.stopImpersonation()

        let responseData = null
        if (response.Data && response.Data.status === 'success') {
          responseData = response.Data.data
        } else if (response.status === 'success') {
          responseData = response.data
        }

        if (!responseData || !responseData.access_token) {
          throw new Error('No access token received for original user')
        }

        this.clearImpersonationState()

        // Restore a regular authenticated session for the original user
        AuthenticationService.setToken(responseData.access_token)

        if (responseData.original_user) {
          AuthenticationService.setUserData(responseData.original_user)
        }

        const storedToken = AuthenticationService.getJwt()
        if (!storedToken || storedToken !== responseData.access_token) {
          throw new Error('Failed to store original user token')
        }

        if (this.$store && responseData.original_user) {
          this.$store.commit('auth/SET_USER', responseData.original_user)
          this.$store.commit('auth/SET_TOKEN', storedToken)
          this.$store.commit('auth/SET_IMPERSONATION', {
            isImpersonating: false,
            originalUser: null,
            impersonatedUser: null
          })
        }

        this.currentUser = responseData.original_user

          this.showToast('success', `Impersonation stopped. Welcome back, ${responseData.original_user.name}!`)

          // Redirect back to own dashboard without a full reload
          await this.$router.push('/my-profile')

      } catch (error) {
        console.error('Error stopping impersonation:', error)

        if (error?.response?.status === 400 || error?.message?.includes('No active impersonation')) {
          this.clearImpersonationState()
          this.showToast?.warning('No active impersonation session found')
          setTimeout(() => window.location.reload(), 1000)
        } else {
          const errorMessage = error?.response?.data?.message ||
            error?.message ||
            'Failed to stop impersonation'
          this.showToast('error', errorMessage)
        }
      }
    },

    async checkImpersonationState() {
      try {
        const token = ApiService.getAuthToken()
        if (!token) {
          this.clearImpersonationState()
          return
        }

        const response = await UserService.getImpersonationStatus()

        let data = null
        if (response?.Data?.status === 'success') {
          data = response.Data.data
        } else if (response?.status === 'success') {
          data = response.data
        }

        if (data && data.is_impersonating) {
          this.isImpersonating = true
          this.originalUser = data.original_user
          this.impersonatedUser = data.impersonated_user
          this.currentUser = data.impersonated_user

          if (this.$store && data.impersonated_user) {
            this.$store.commit('auth/SET_USER', data.impersonated_user)
            this.$store.commit('auth/SET_IMPERSONATION', {
              isImpersonating: true,
              originalUser: data.original_user,
              impersonatedUser: data.impersonated_user
            })
          }

          localStorage.setItem('impersonation_active', 'true')
          localStorage.setItem('impersonation_data', JSON.stringify({
            original_user: data.original_user,
            impersonated_user: data.impersonated_user
          }))
        } else {
          this.clearImpersonationState()
        }
      } catch (error) {
        console.error('Error checking impersonation state:', error)
        this.clearImpersonationState()
      }
    },

    async refreshAllData() {
      try {
        await this.checkImpersonationState()
        await this.fetchCurrentUser()
        await this.fetchUsers(false)
      } catch (error) {
        console.error('Error refreshing data:', error)
      }
    },

    async refreshAuthorizationData() {
      await this.fetchUsers(false)
      await this.loadWorkingGroups()
    },

    clearImpersonationState() {
      this.isImpersonating = false
      this.impersonatedUser = null
      this.originalUser = null
      localStorage.removeItem('impersonation_active')
      localStorage.removeItem('impersonation_data')
      localStorage.removeItem('original_user')
      localStorage.removeItem('impersonated_user')

      if (this.$store) {
        this.$store.commit('auth/SET_IMPERSONATION', {
          isImpersonating: false,
          originalUser: null,
          impersonatedUser: null
        })
      }
    },

    // ==========================
    // USER CRUD OPERATIONS
    // ==========================

    showAddUserModal() {
      this.editingUser = null
      this.clearForm()
      this.showUserModal = true
    },

    editUser(user) {
      this.editingUser = user

      console.log("🔍 Editing User:", JSON.parse(JSON.stringify(user)));

      // Base user fields
      this.userForm = {
        name: user.name,
        email: user.email,
        password: '',
        password_confirmation: '',
        title: this.normalizeTitle(user.title),
        avatar: user.avatar || ''
      }

      // Normalise fellowship_details to an array (can be object or array from API)
      let fellowshipArray = []

      if (Array.isArray(user.fellowship_details)) {
        fellowshipArray = user.fellowship_details
      } else if (user.fellowship_details) {
        fellowshipArray = [user.fellowship_details]
      }

      // Limit to max 2 entries
      fellowshipArray = fellowshipArray.slice(0, 2)

      if (fellowshipArray.length === 0) {
        this.fellowshipEntries = [this.getEmptyFellowshipEntry()]
      } else {
        const normalizedEntries = fellowshipArray.map(f => ({
          id: f.id || null,
          gender: this.normalizeGender(f.gender),
          edctp_programme: this.normalizeProgramme(f.edctp_programme),
          edctp_call: this.normalizeCall(f.edctp_project_call || f.edctp_call),
          edctp_call_date: this.normalizeYear(f.edctp_call_date),
          edctp_project_title: f.edctp_project_title || '',
          edctp_project_code: f.edctp_project_code || f.edctp_project || '',
          start_date: this.formatDate(f.edctp_project_start_date || f.start_date),
          end_date: this.formatDate(f.edctp_project_end_date || f.end_date),
          objectives: f.objectives || '',
          study_design: f.study_design || '',
          project_summary: f.project_summary || ''
        }));

        const firstGender = normalizedEntries.find(e => e.gender)?.gender || '';
        if (firstGender) {
          normalizedEntries.forEach(e => {
            if (!e.gender) e.gender = firstGender;
          });
        }

        this.fellowshipEntries = normalizedEntries;

        // If there is exactly 1 entry and its programme is 1,
        // we only keep 1 tab. A second will be created when
        // it is changed to EDCTP2.
        if (
          this.fellowshipEntries.length === 1 &&
          String(this.fellowshipEntries[0].edctp_programme) === '2'
        ) {
          // Allowed to add second entry later when needed,
          // but we don't force-create here – backend may already
          // have only one EDCTP2 record.
        }
      }

      this.activeFellowshipTab = 0
      this.showUserModal = true
    },

    async saveUser() {
      this.saving = true
      try {
        // Primary fellowship entry (first tab)
        const primary = this.fellowshipEntries[0] || this.getEmptyFellowshipEntry()
        const secondary = this.fellowshipEntries[1] || null

        const formData = {
          // base user fields
          name: this.userForm.name,
          email: this.userForm.email,
          title: this.userForm.title,

          // primary fellowship fields (same names as before)
          gender: primary.gender,
          edctp_programme: this.normalizeProgramme(primary.edctp_programme),
          edctp_call: primary.edctp_call,
          edctp_project_title: primary.edctp_project_title,
          edctp_project_code: primary.edctp_project_code,
          start_date: primary.start_date,
          end_date: primary.end_date,
          objectives: primary.objectives,
          study_design: primary.study_design,
          project_summary: primary.project_summary,
          callDate: primary.edctp_call_date
            ? String(primary.edctp_call_date)
            : null,
          // Extra: pass the fellowship ID so backend can update instead of always creating
          fellowship_id: primary.id || null,

          // Additional data for second entry (if allowed)
          secondary_fellowship: secondary
            ? {
              id: secondary.id || null,
              gender: secondary.gender,
              edctp_programme: this.normalizeProgramme(secondary.edctp_programme),
              edctp_call: secondary.edctp_call,
              callDate: secondary.edctp_call_date
                ? String(secondary.edctp_call_date)
                : null,
              edctp_project_title: secondary.edctp_project_title,
              edctp_project_code: secondary.edctp_project_code,
              start_date: secondary.start_date,
              end_date: secondary.end_date,
              objectives: secondary.objectives,
              study_design: secondary.study_design,
              project_summary: secondary.project_summary
            }
            : null,

          // Static defaults you had before
          collaborators: [],
          sites: [],
          participants: [],
          number_of_subjects: '',
          status: 'active',
          results_and_outcomes: '',
          phd_study: [],
          publications: [],
          disease_area: '',
          students: []
        }

        if (this.userForm.password) {
          formData.password = this.userForm.password
          formData.password_confirmation = this.userForm.password
        }

        let response
        const isEditing = !!this.editingUser

        if (this.editingUser) {
          response = await UserService.updateUser(this.editingUser.id, formData)
        } else {
          response = await UserService.createFellow(formData)
        }

        const isSuccess =
          response?.Data?.status === 'success' || response?.status === 'success'

        if (isSuccess) {
          // If a cropped image was queued for a new fellow, upload it now
          if (!isEditing && this.pendingCroppedBase64) {
            const newUserId =
              response?.Data?.data?.id ||
              response?.data?.id
            if (newUserId) {
              await this.uploadCroppedAvatar(newUserId, this.pendingCroppedBase64)
            }
            this.pendingCroppedBase64 = null
          }

          this.clearForm()
          this.editingUser = null
          this.showUserModal = false
          await this.refreshAuthorizationData()
          this.showToast(
            'success',
            isEditing ? 'User updated successfully!' : 'Fellow created successfully!'
          )
        } else if (response.Data.errors.email){
          throw new Error('Email Already Exist!')
        } else {
          throw new Error('Save operation failed')
        }
      } catch (error) {
        console.error('Error saving user:', error)

        const errorMessage =
          error?.response?.data?.message || error?.message || 'Failed to save user'

        if (error?.response?.data?.errors) {
          const errors = error.response.data.errors
          Object.keys(errors).forEach(key => {
            this.showToast('error', errors[key][0])
          })
        } else {
          this.showToast('error', errorMessage)
        }
      } finally {
        this.saving = false
      }
    },

    deleteUser(user) {
      this.userToDelete = user
      this.showDeleteModalFlag = true
    },

    async confirmDeleteUser() {
      if (!this.userToDelete) return

      try {
        const response = await UserService.deleteUser(this.userToDelete.id)
        const isSuccess = response?.Data?.status === 'success' || response?.status === 'success'

        if (isSuccess) {
          this.showDeleteModalFlag = false
          this.userToDelete = null
          await this.refreshAuthorizationData()
          this.showToast('success', 'User disabled successfully!')
        } else {
          throw new Error('User deletion failed')
        }
      } catch (error) {
        console.error('Error deleting user:', error)
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to disable user'
        this.showToast('error', errorMessage)
      }
    },

    async restoreUser(user) {
      try {
        const response = await UserService.restoreUser(user.id)
        const isSuccess = response?.Data?.status === 'success' || response?.status === 'success'

        if (isSuccess) {
          await this.refreshAuthorizationData()
          this.showToast('success', 'User restored successfully!')
        } else {
          throw new Error('User restoration failed')
        }
      } catch (error) {
        console.error('Error restoring user:', error)
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to restore user'
        this.showToast('error', errorMessage)
      }
    },

    clearForm() {
      this.userForm = {
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        title: '',
        avatar: ''
      }

      this.fellowshipEntries = [this.getEmptyFellowshipEntry()]
      this.activeFellowshipTab = 0
      this.editingUser = null
      this.pendingCroppedBase64 = null
    },

    formatDateForInput(dateString) {
      if (!dateString) return ''
      return dateString.split('T')[0]
    },

    normalizeProgramme(value) {
      if (!value) return '';

      const v = String(value).toLowerCase().trim();

      // Perfect matches
      if (v === '1') return '1';
      if (v === '2') return '2';

      // Common stored formats
      if (v === 'edctp1') return '1';
      if (v === 'edctp2') return '2';

      // Sometimes stored nicely as EDCTP1 / EDCTP2
      if (v.includes('edctp1')) return '1';
      if (v.includes('edctp2')) return '2';

      // If any number exists inside
      if (v.includes('1')) return '1';
      if (v.includes('2')) return '2';

      return '';
    },

    formatDate(dateValue) {
      if (!dateValue) return '';
      const d = new Date(dateValue);

      if (isNaN(d)) return '';

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },

    normalizeTitle(raw) {
      if (!raw) return '';
      const value = raw.toString().trim();
      const lower = value.toLowerCase();

      if (['mr', 'mr.'].includes(lower)) return 'Mr';
      if (['mrs', 'mrs.'].includes(lower)) return 'Mrs';
      if (['ms', 'ms.'].includes(lower)) return 'Ms';
      if (['dr', 'dr.', 'doctor'].includes(lower)) return 'Dr';

      if ([
        'associate professor',
        'assoc prof',
        'assoc. prof',
        'assoc professor',
        'assoc. professor'
      ].includes(lower)) {
        return 'Associate Professor';
      }

      if ([
        'prof',
        'prof.',
        'professor'
      ].includes(lower)) {
        return 'Professor';
      }

      if (['public'].includes(lower)) return 'Public';

      // Fallback – keep whatever is in DB if we don't know it
      return value;
    },

    normalizeGender(value) {
      if (!value) return '';

      const v = value.toString().trim().toLowerCase();

      if (v === 'm' || v === 'male') return 'male';
      if (v === 'f' || v === 'female') return 'female';
      if (v === 'o' || v === 'other') return 'other';

      // If DB already stored 'male'/'female' etc, just return it
      if (['male', 'female', 'other'].includes(v)) return v;

      return ''; // unknown → no selection
    },

    normalizeCall(value) {
      if (!value) return '';

      const v = String(value).trim();

      // Already correct (1–8)
      if (/^[1-8]$/.test(v)) return v;

      // Try to map text → number
      const map = {
        "Career Development Fellowship (CDF)": "1",
        "Senior Fellowship (SF)": "2",
        "EDCTP Clinical Research & Development Fellowship (R&D F)": "3",
        "EDCTP-AREF Preparatory Fellowships (PF)": "4",
        "Senior Fellowship Plus (SFP)": "5",
        "Disease Outbreak and Epidemic Response (EPI Fellows)": "6",
        "Female PhD Fellowships": "7",
        "EDCTP Master's Fellowship": "8"
      };

      return map[v] || '';
    },

    // ==========================
    // ROLE & WORKING GROUP
    // ==========================

    showAssignRoleModal(user) {
      this.selectedUserForRole = user
      this.newRoleForUser = ''
      this.roleChangeReason = ''
      this.showAssignRoleModalFlag = true
    },

    async assignRole() {
      if (!this.selectedUserForRole || !this.newRoleForUser) return

      try {
        const response = await UserService.assignRole(this.selectedUserForRole.id, {
          role: this.newRoleForUser,
          reason: this.roleChangeReason
        })

        const isSuccess = response?.Data?.status === 'success' || response?.status === 'success'

        if (isSuccess) {
          this.showAssignRoleModalFlag = false
          this.selectedUserForRole = null
          this.newRoleForUser = ''
          this.roleChangeReason = ''
          await this.refreshAuthorizationData()
          this.showToast('success', 'Role assigned successfully!')
        } else {
          throw new Error('Role assignment failed')
        }
      } catch (error) {
        console.error('Error assigning role:', error)
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to assign role'
        this.showToast('error', errorMessage)
      }
    },

    showAssignWorkingGroupModal(user) {
      this.selectedUserForWorkingGroup = user;

      this.selectedWorkingGroups = (user.workingGroups || [])
        .map(wg => typeof wg === "object" ? wg.id : wg);

      this.workingGroupAssignmentReason = '';
      this.showAssignWorkingGroupModalFlag = true;
    },

    async assignWorkingGroup() {
      if (!this.selectedUserForWorkingGroup) return;

      try {
        if (this.selectedWorkingGroups.length === 0) {
          this.showToast?.warning("⚠ Please select at least one working group.");
          return;
        }

        // Assign user as admin to selected groups
        const responses = await Promise.all(
          this.selectedWorkingGroups.map(wgId =>
            WorkingGroupService.addWorkingGroupAdmin(wgId, {
              user_id: this.selectedUserForWorkingGroup.id
            })
          )
        );

        // Check if backend returned messages per assignment
        const successMessages = [];
        const warningMessages = [];

        responses.forEach(res => {
          const message = res?.message || res?.Data?.message || "Updated.";

          if (message.toLowerCase().includes("not") || message.toLowerCase().includes("denied")) {
            warningMessages.push(message);
          } else {
            successMessages.push(message);
          }
        });

        // Show appropriate UI messages
        if (successMessages.length > 0) {
          this.showToast('success', '✅ Successfully updated working groups.');
        }

        if (warningMessages.length > 0) {
          warningMessages.forEach(msg =>
            this.showToast('error', 'User role is not correct (Must be EDCTP Admin)')
          );
        }

        // Refresh users so UI updates
        await this.refreshAuthorizationData();

        // Close modal
        this.showAssignWorkingGroupModalFlag = false;
        this.selectedWorkingGroups = [];
        this.selectedUserForWorkingGroup = null;
        this.workingGroupAssignmentReason = "";

      } catch (error) {
        console.error("❌ Error assigning working groups:", error);

        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to assign working groups.";

        this.showToast('error', 'User role is not correct (Must be EDCTP Admin)');
      }
    },

    async sendPasswordReset(user) {
      try {
        const response = await UserService.sendPasswordReset(user.email)
        const isSuccess = response?.Data?.status === 'success' || response?.status === 'success'

        if (isSuccess) {
          this.showToast('success', 'Password reset link sent successfully!')
          await this.refreshAuthorizationData()
        } else {
          throw new Error('Password reset failed')
        }
      } catch (error) {
        console.error('Error sending password reset:', error)
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to send password reset email'
        this.showToast('error', errorMessage)
      }
    },

    // ==========================
    // AVATAR UPLOAD
    // ==========================

    getModalAvatarUrl() {
      if (this.userForm.avatar) {
        if (this.userForm.avatar.startsWith('http')) {
          return this.userForm.avatar
        }
        if (this.userForm.avatar.startsWith('/storage/')) {
          return `${this.apiBaseUrl}${this.userForm.avatar}`
        }
        return this.userForm.avatar
      }

      if (this.editingUser) {
        return this.getUserAvatar(this.editingUser)
      }

      const userName = this.userForm.name || 'User'
      return this.generateFallbackAvatar(userName)
    },

    onModalAvatarError(event) {
      console.warn('Modal avatar failed to load:', event.target.src)

      if (this.editingUser) {
        this.onAvatarError(event, this.editingUser)
      } else {
        const userName = this.userForm.name || 'User'
        event.target.src = this.generateFallbackAvatar(userName)
      }
    },

    onAvatarFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return

      // Reset input so the same file can be re-selected
      event.target.value = ''

      const reader = new FileReader()
      reader.onload = (e) => {
        this.cropperImageSrc = e.target.result
        this.showCropper = true
      }
      reader.readAsDataURL(file)
    },

    async onImageCropped(base64DataUrl) {
      if (this.editingUser) {
        // Editing an existing fellow — upload immediately
        await this.uploadCroppedAvatar(this.editingUser.id, base64DataUrl)
      } else {
        // Creating a new fellow — queue the image; uploads after save
        this.pendingCroppedBase64 = base64DataUrl
      }
    },

    async uploadCroppedAvatar(userId, base64DataUrl) {
      try {
        this.saving = true

        const response = await UserService.uploadAvatarBase64(userId, base64DataUrl)
        const isSuccess = response?.Data?.status === 'success' || response?.status === 'success'

        if (isSuccess) {
          const avatarUrl = response?.Data?.data?.avatar_url || response?.data?.avatar_url

          if (avatarUrl) {
            this.userForm.avatar = avatarUrl

            const userIndex = this.users.findIndex(u => u.id === userId)
            if (userIndex !== -1) {
              this.users[userIndex].avatar = avatarUrl
            }

            if (this.editingUser && this.editingUser.id === userId) {
              this.editingUser.avatar = avatarUrl
            }
          }

          this.showToast('success', 'Profile image set successfully!')
          this.pendingCroppedBase64 = null
        } else {
          throw new Error('Profile image upload failed')
        }
      } catch (error) {
        console.error('Error uploading cropped avatar:', error)
        const errorMessage =
          error?.response?.data?.Data?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to upload profile image'
        this.showToast('error', errorMessage)
      } finally {
        this.saving = false
      }
    },

    // ==========================
    // AUDIT LOG
    // ==========================

    async fetchAuditLogs() {
      try {
        this.resetPagination()

        const params = {}

        if (this.auditActionFilter) {
          params.action = this.auditActionFilter
        }

        if (this.auditDateFrom) {
          params.date_from = this.auditDateFrom
        }

        if (this.auditDateTo) {
          params.date_to = this.auditDateTo
        }

        const response = await UserService.getAuditLogs(params)

        if (response?.Data?.status === 'success' && response.Data.data) {
          this.auditLogs = response.Data.data.data || response.Data.data
        } else if (response?.status === 'success' && response.data) {
          this.auditLogs = response.data.data || response.data
        } else if (Array.isArray(response?.data)) {
          this.auditLogs = response.data
        } else {
          this.auditLogs = []
        }
      } catch (error) {
        console.error('Error fetching audit logs:', error)
        this.auditLogs = []
      }
    },

    async exportAuditLog() {
      try {
        const params = {}

        if (this.auditActionFilter) {
          params.action = this.auditActionFilter
        }

        if (this.auditDateFrom) {
          params.date_from = this.auditDateFrom
        }

        if (this.auditDateTo) {
          params.date_to = this.auditDateTo
        }

        const response = await UserService.exportAuditLogs(params)

        const blob = new Blob([response], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.showToast('success', 'Audit log exported successfully!')
      } catch (error) {
        console.error('Error exporting audit logs:', error)
        this.showToast('error', 'Failed to export audit logs')
      }
    },

    getEmptyFellowshipEntry() {
      return {
        id: null,
        gender: '',
        edctp_programme: '',
        edctp_call: '',
        edctp_call_date: '',
        edctp_project_title: '',
        edctp_project_code: '',
        start_date: '',
        end_date: '',
        objectives: '',
        study_design: '',
        project_summary: ''
      }
    },

    /**
     * EDCTP programme change logic (per tab)
     * Rules:
     * - Max 2 fellowship entries total.
     * - If there is only 1 entry and it is changed from 1 -> 2,
     *   create a second blank entry.
     * - If there are already 2 entries, never create a third.
     */
    onProgrammeChange(index) {
      const entry = this.fellowshipEntries[index]
      if (!entry) return

      // Count how many entries are EDCTP2
      const programme2Count = this.fellowshipEntries.filter(
        f => String(f.edctp_programme) === '2'
      ).length

      // If only one entry exists and it has just been set to EDCTP2,
      // and there is still room, create a second entry.
      if (
        this.fellowshipEntries.length === 1 &&
        String(entry.edctp_programme) === '2' &&
        programme2Count === 1
      ) {
        // Create a second blank entry
        if (this.fellowshipEntries.length < 2) {
          this.fellowshipEntries.push(this.getEmptyFellowshipEntry())
          this.activeFellowshipTab = 1
        }
      }

      // If we already have 2 entries and one is changed to 2 again,
      // do nothing extra – no third entry.
    },

    viewAuditDetails(log) {
      this.selectedAuditLog = log
      this.showAuditDetailsModal = true
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.auditCurrentPage) {
        this.auditCurrentPage = page
      }
    },

    changePageSize() {
      this.auditCurrentPage = 1
    },

    resetPagination() {
      this.auditCurrentPage = 1
    },

    // ==========================
    // UTILITY
    // ==========================

    formatDateTime(dateString) {
      return new Date(dateString).toLocaleString()
    },

    getActionBadgeClass(action) {
      const classes = {
        'login': 'bg-success',
        'logout': 'bg-info',
        'create': 'bg-primary',
        'update': 'bg-warning',
        'delete': 'bg-danger',
        'impersonate': 'bg-purple'
      }
      return classes[action] || 'bg-secondary'
    }
  },

  watch: {
    auditActionFilter() {
      this.resetPagination()
    },

    auditDateFrom() {
      this.resetPagination()
    },

    auditDateTo() {
      this.resetPagination()
    },

    userSearch() {
      this.resetPagination()
      this.closeActionMenu()
    },

    userRoleFilter() {
      this.usersPage = 1
      this.closeActionMenu()
    }
  }
}
</script>

<style scoped>
.authorization-page {
  padding: 1rem 0;
}
.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}
.nav-tabs .nav-link {
  cursor: pointer;
}
.nav-tabs .nav-link.active {
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
}
.tab-content {
  min-height: 400px;
}
.form-check-input {
  margin-top: 0.125rem;
}
.card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.profile-image-container {
  position: relative;
  display: inline-block;
}
.card-header {
  background-color: #f8f9fa;
  font-weight: 600;
}
.btn-info {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}
.btn-info:hover {
  background-color: #138496;
  border-color: #117a8b;
}
.modal-lg {
  max-width: 800px;
}
.form-control:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}
.form-select:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}
textarea.form-control {
  resize: vertical;
  min-height: 100px;
}
.alert-warning {
  border-left: 4px solid #ffc107;
}
.bg-purple {
  background-color: #6f42c1 !important;
}
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
.spinner-border {
  width: 2rem;
  height: 2rem;
}
.text-center {
  text-align: center;
}
.py-5 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}
.modal-lg {
  max-width: 800px;
}

.info-card {
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  padding: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.table th {
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #dee2e6;
}

.actions-cell {
  width: 1%;
  white-space: nowrap;
}

.action-menu-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.authorization-action-menu {
  display: block;
  z-index: 2000;
  max-height: min(60vh, 24rem);
  overflow-y: auto;
  box-shadow: 0 0.75rem 2rem rgba(15, 23, 42, 0.18);
}

@media (max-width: 768px) {
  .modal-lg {
    max-width: 95%;
    margin: 1rem auto;
  }
}

/*
    Pagination styling
*/

.audit-table-container {
  min-height: 400px;
}

.pagination-info {
  flex: 1;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination .page-link {
  color: #009CAD;
  border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
  background-color: #009CAD;
  border-color: #009CAD;
  color: white;
}

.pagination .page-link:hover {
  color: #009CAD;
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d;
  background-color: #fff;
  border-color: #dee2e6;
  opacity: 0.5;
}

.form-select-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 1rem;
  }

  .pagination-info,
  .page-size-selector {
    text-align: center;
  }

  .pagination {
    justify-content: center;
  }
}
</style>
